import React, { useEffect, useRef, useState } from 'react';
import { ClickAwayListener, Popper } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { HdButton, HdIcon, HdIconButton } from '../../../../components';
import useSettingsStorageService from '../../../../auth/hooks/useSettingsStorageService';
import useUserService from '../../../../auth/hooks/useUserService';
import { isSettingsRoute } from '../../../../layout/constants/settingsNav';
import {
  SETTINGS_MOVED_AT,
  SETTINGS_NAV_GUIDE_DELAY,
  SETTINGS_NAV_GUIDE_SHOWN_KEY
} from '../../../constants/settingsNavGuide';
import styles from './SettingsNavGuide.module.scss';

export const dataIds = {
  base: 'settings-nav-guide',
  close: 'settings-nav-guide-close',
  dismiss: 'settings-nav-guide-dismiss'
};

// Accepts seconds or milliseconds; an unreadable value counts as new, so the guide stays away.
function isCreatedBeforeSettingsMoved(createdTs: unknown): boolean {
  const value = Number(createdTs);

  if (!Number.isFinite(value) || value <= 0) {
    return false;
  }

  const createdAtMs = value < 1e12 ? value * 1000 : value;

  return createdAtMs < SETTINGS_MOVED_AT;
}

interface SettingsNavGuideProps {
  /** CSS selector for the appbar item the popover points at. */
  target: string;
  /** Lets the appbar light that item up while the pointer is open. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * One-time pointer to where Account, Team, Billing and Alerts went, shown only to users who
 * knew the old profile menu. X, "Got it" and any visit to Settings write the flag through SettingsStorageService,
 * which keys local storage per user — so it never returns for them, and a shared browser does
 * not silence it for the next person. A click elsewhere or Escape only hides it until the next load.
 */
export function SettingsNavGuide({ target, onOpenChange }: SettingsNavGuideProps) {
  const { getSettings, applySettings } = useSettingsStorageService();
  const { user } = useUserService();
  const { pathname } = useLocation();
  /*
   * The anchor element is the open flag: resolved from the DOM once the appbar has painted,
   * so the popper always has something to measure. The toolkit's menu popper was doing that
   * resolution during its own render and parking the card away from the item.
   */
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [caretEl, setCaretEl] = useState<HTMLElement | null>(null);
  const isHiddenForSession = useRef(false);

  const isOnSettingsRoute = isSettingsRoute(pathname);

  useEffect(() => {
    // Already dismissed, the user is in Settings and has plainly found it, or the account is
    // newer than the move and never saw Settings anywhere else.
    const isNewUser = !isCreatedBeforeSettingsMoved(user?.createdTs);

    // Reaching Settings is what the guide asks for, so it counts as a permanent dismissal.
    if (isOnSettingsRoute) {
      isHiddenForSession.current = true;

      if (!getSettings(SETTINGS_NAV_GUIDE_SHOWN_KEY)) {
        rememberDismissal();
      }
    }

    if (isHiddenForSession.current || getSettings(SETTINGS_NAV_GUIDE_SHOWN_KEY) || isOnSettingsRoute || isNewUser) {
      setAnchorEl(null);
      return undefined;
    }

    const timer = window.setTimeout(() => setAnchorEl(document.querySelector(target)), SETTINGS_NAV_GUIDE_DELAY);

    return () => window.clearTimeout(timer);
  }, [user?.createdTs, isOnSettingsRoute]);

  useEffect(() => {
    onOpenChange?.(!!anchorEl);
  }, [anchorEl]);

  function rememberDismissal() {
    try {
      applySettings(SETTINGS_NAV_GUIDE_SHOWN_KEY, true);
    } catch {
      // Storage full or blocked: the guide still closes and may return on the next load.
    }
  }

  const dismiss = () => {
    setAnchorEl(null);
    rememberDismissal();
  };

  const hideForSession = () => {
    isHiddenForSession.current = true;
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!anchorEl) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideForSession();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [anchorEl]);

  if (!anchorEl) {
    return null;
  }

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement='right'
      className={styles.popper}
      modifiers={[
        { name: 'offset', options: { offset: [0, 12] } },
        // Popper places the caret itself, so it keeps pointing at the item even if the
        // card shifts to stay inside the viewport.
        { name: 'arrow', options: { element: caretEl, padding: 12 } }
      ]}
    >
      <ClickAwayListener onClickAway={hideForSession}>
        <div className={styles.guide} data-id={dataIds.base}>
          <span ref={setCaretEl} className={styles.caret} />

          <div className={styles.header}>
            <span className='text-label-small'>Settings moved here</span>

            <HdIconButton dataId={dataIds.close} onClick={dismiss} aria-label='Close'>
              <HdIcon name='close' size={2} />
            </HdIconButton>
          </div>

          <p className={styles.body}>
            Account, Team, Billing and Alerts have left the profile menu. They now live together under Settings.
          </p>

          <div className={styles.actions}>
            <HdButton size='sm' dataId={dataIds.dismiss} onClick={dismiss}>
              Got it
            </HdButton>
          </div>
        </div>
      </ClickAwayListener>
    </Popper>
  );
}
